// Deleting a note's Firestore document does not touch Cloud Storage, and no
// server trigger does either: its handwriting (`annotations.ink`,
// `mindmap.ink`), pictures and imported files under
// `users/<uid>/notes/<noteID>/` would stay there for good. iOS
// NoteStorageCleanup.swift, as Windows ports it (`deleteNoteAssets`, which
// also walks sub-folders): called only once a note is permanently deleted —
// never for the trash, which can be restored — and only after Firestore
// confirmed it.
//
// Best effort: a failure leaves the objects orphaned, the state before this
// existed. The Storage calls come in as `api` ({ storage, ref, listAll,
// deleteObject } of firebase/storage), so the walk runs without the SDK in
// tests.

/** `users/<ownerID>/notes/<noteID>`, or null when either id could widen
 *  the folder: an empty id would make it `users/<uid>/notes/`, every note. */
export function noteAssetsPath(ownerID, noteID) {
  const safe = (id) => typeof id === "string" && id.length > 0 && !id.includes("/") && id !== "." && id !== "..";
  return safe(ownerID) && safe(noteID) ? `users/${ownerID}/notes/${noteID}` : null;
}

/** Deletes every object under `folder` and its sub-folders; resolves with
 *  how many went. A file that will not go is left and the rest carry on. */
export async function deleteFolder(folder, api) {
  const listing = await api.listAll(folder);
  const results = await Promise.all(
    listing.items.map((item) =>
      api.deleteObject(item).then(
        () => 1,
        (error) => {
          console.warn("[NoteStorageCleanup]", item.fullPath, error && error.message);
          return 0;
        },
      ),
    ),
  );
  const nested = await Promise.all(
    listing.prefixes.map((prefix) =>
      deleteFolder(prefix, api).catch((error) => {
        console.warn("[NoteStorageCleanup] list", prefix.fullPath, error && error.message);
        return 0;
      }),
    ),
  );
  return [...results, ...nested].reduce((sum, n) => sum + n, 0);
}

/** Everything stored for a note that is gone for good. Never rejects. */
export async function deleteNoteAssets(ownerID, noteID, api) {
  const path = noteAssetsPath(ownerID, noteID);
  if (!path) return 0;
  try {
    return await deleteFolder(api.ref(api.storage, path), api);
  } catch (error) {
    console.warn("[NoteStorageCleanup] list", path, error && error.message);
    return 0;
  }
}
